import { useCallback, useEffect, useState } from "react";

const SESSION_QUIZ_KEY = "quiz-session";

function getStored<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = sessionStorage.getItem(key);
    if (raw == null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function setStored(key: string, value: unknown): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore
  }
}

/**
 * Persist a value in sessionStorage, keyed by a sub-key under the quiz session.
 * Returns [value, setValue] like useState; updates both state and sessionStorage.
 */
export function useSessionStorage<T>(
  subKey: string,
  initialValue: T
): [T, (value: T | ((prev: T) => T)) => void] {
  const key = `${SESSION_QUIZ_KEY}:${subKey}`;
  const [state, setState] = useState<T>(() => getStored(key, initialValue));

  const setValue = useCallback(
    (value: T | ((prev: T) => T)) => {
      setState((prev) => {
        const next = typeof value === "function" ? (value as (p: T) => T)(prev) : value;
        setStored(key, next);
        return next;
      });
    },
    [key]
  );

  useEffect(() => {
    const stored = getStored(key, initialValue);
    setState(stored);
  }, [key]); // re-hydrate from another tab/window if needed

  return [state, setValue];
}

/** Clear all quiz-related keys from sessionStorage */
export function clearQuizSession(): void {
  if (typeof window === "undefined") return;

  const toRemove: string[] = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const k = sessionStorage.key(i);
    if (k?.startsWith(SESSION_QUIZ_KEY)) toRemove.push(k);
  }
  toRemove.forEach((k) => sessionStorage.removeItem(k));
}
